import { infiniteQueryOptions, useInfiniteQuery } from '@tanstack/react-query';
import { WS_INVALIDATED_QUERY_STALE_TIME } from '../constants';
import { operations, SchemaOrderFillDto } from '../generated/apiTypes';
import { dataOrThrow, fetchClient } from '../lib/apiClient';
import { Gwei } from '../lib/gwei';
import { removeUndefined } from '../lib/objectHelpers';

const transformFill = (fill: SchemaOrderFillDto) => ({
  ...fill,
  feeUsd: Gwei.toGwei(fill.feeUsd),
  filled: Gwei.toGwei(fill.filled),
  price: Gwei.toGwei(fill.price),
  reduceOnly: fill.reduceOnly,
});

export type Fill = ReturnType<typeof transformFill>;

export type FillParams = Omit<
  Omit<operations['OrderController_listFillsBySubaccountId']['parameters']['query'], 'cursor'>,
  'includeSelfTrades'
>;

export const fillsQuery = ({ subaccountId, ...params }: FillParams) => {
  return infiniteQueryOptions({
    queryKey: ['GET', '/v1/order/fill', { subaccountId }, params] as const,
    enabled: Boolean(subaccountId),
    initialPageParam: '',
    queryFn: async ({ pageParam, queryKey, signal }) => {
      const [method, url] = queryKey;
      const { data } = await fetchClient[method](url, {
        signal,
        params: {
          query: removeUndefined({
            subaccountId,
            ...params,
            includeSelfTrades: false,
            cursor: pageParam || undefined,
          }),
        },
      }).then(dataOrThrow);

      return {
        data: data?.data.map(transformFill) ?? null,
        hasNext: data?.hasNext || false,
        nextCursor: data?.nextCursor,
      };
    },
    staleTime: WS_INVALIDATED_QUERY_STALE_TIME,
    getNextPageParam: (lastPage) => (lastPage.hasNext ? lastPage.nextCursor : undefined),
  });
};

export const useFills = (params: FillParams) => useInfiniteQuery(fillsQuery(params));
